/**
 * StaffCMS — iter-62.
 *
 * Page-first CMS hub. Replaces the section-first `StaffSiteSettings` UI
 * as the canonical entry-point for site editing.
 *
 * The hub is organised page-by-page (Accueil, Hôtel, Beach Club, Le Kaai,
 * Lounge, Loazy, Événements, Contact, Blog, Boutique, Global).
 * Each page shows the sections that compose it (with a live indicator of
 * whether it's already fully CMS-driven or still partially hardcoded) and
 * a set of *direct links* to the specialised editors that already exist
 * elsewhere in the dashboard (Blog, MediaLibrary, Livret PDF, Menus PDF,
 * Memberships, Loisirs activities, Meeting rooms, Hébergement).
 *
 * When a section is section-based (stored in `site_settings`), the editor
 * expands *inline* using the very same `<SectionEditor>` that lived in
 * `StaffSiteSettings.jsx` — no code duplication.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Save, RefreshCw, Undo2, AlertCircle, History, ExternalLink,
  ChevronDown, ChevronRight, FileText, Image as ImageIcon, Video, BookOpen,
  Home as HomeIcon, Bed, Utensils, GlassWater, Palmtree, PartyPopper,
  Sparkles, CreditCard, ShoppingBag, PenLine, Mail, Settings, CheckCircle2, Circle,
} from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { invalidateSiteConfig } from "../../lib/site-config";
import { SectionEditor } from "./StaffSiteSettings";

// ── Page-based map. Each page declares :
//   • the site_settings sections that back it (`sections`)
//   • the specialised staff editors it also relies on (`shortcuts`)
// ────────────────────────────────────────────────────────────────
const PAGES = [
  {
    key: "accueil",
    label: "Accueil",
    icon: HomeIcon,
    intro: "La vitrine principale de BBr — hero, cartes d'expériences, Le Kaai, Mot du Fondateur, FAQ.",
    sections: [
      { key: "hero", label: "Hero (vidéo + titre)" },
      { key: "experiences_choice", label: "Choisissez votre expérience" },
      { key: "univers", label: "Univers (6 cartes)" },
      { key: "home_kaai_teaser", label: "Bloc Le Kaai (teaser immersif)" },
      { key: "founder_word", label: "Mot du Fondateur" },
      { key: "faq", label: "FAQ" },
      { key: "testimonials", label: "Témoignages" },
      { key: "instagram", label: "Instagram" },
    ],
    shortcuts: [],
  },
  {
    key: "hotel",
    label: "Hôtel / Hébergement",
    icon: Bed,
    intro: "Chambres, suites, tarifs et galeries. Gérez chaque hébergement individuellement.",
    sections: [
      { key: "hero_hebergement", label: "En-tête immersif (image + textes)" },
      { key: "hebergement_fiches", label: "Fiches techniques — Chambre Exécutive & Suites" },
      { key: "offers", label: "Prix & disponibilités (Hébergement)" },
    ],
    shortcuts: [
      { to: "/staff/hebergement", label: "Gérer les hébergements (chambres, suites)", icon: Bed },
      { to: "/staff/media-library", label: "Bibliothèque médias — galeries chambres", icon: ImageIcon },
    ],
  },
  {
    key: "beach-club",
    label: "Beach Club",
    icon: Palmtree,
    intro: "Day Pass, Sunset Experience, B Brunch et toutes les nouvelles expériences Beach Life.",
    sections: [
      { key: "hero_beach_club", label: "En-tête immersif (image + textes)" },
      { key: "offers", label: "Offres & prix (Day Pass · Sunset · Brunch)" },
    ],
    shortcuts: [
      { to: "/staff/activities-config", label: "Configurer les créneaux & capacités", icon: Settings },
    ],
  },
  {
    key: "loazy",
    label: "Loazy · Loisirs",
    icon: Sparkles,
    intro: "Activités lagunaires — jet ski, paddle, kayak, sports & terrains.",
    sections: [
      { key: "hero_activites", label: "En-tête immersif (image + textes)" },
      { key: "offers", label: "Prix des activités" },
    ],
    shortcuts: [
      { to: "/staff/loisirs-activities", label: "Gérer les activités Loisirs", icon: Sparkles },
    ],
  },
  {
    key: "events",
    label: "Événements",
    icon: PartyPopper,
    intro: "Créez et pilotez les événements ponctuels : dates, prix, capacités, images.",
    sections: [
      { key: "hero_events", label: "En-tête + état vide" },
    ],
    shortcuts: [
      { to: "/staff/events-pipeline", label: "Pipeline événements", icon: PartyPopper },
      { to: "/staff/media-library", label: "Uploader visuels d'événement", icon: ImageIcon },
    ],
  },
  {
    key: "le-kaai",
    label: "Restaurant Le Kaai",
    icon: Utensils,
    intro: "Menu PDF, tarifs, traversée & créneaux.",
    sections: [
      { key: "hero_le_kaai", label: "En-tête immersif (image + textes)" },
      { key: "offers", label: "Prix menu Le Kaai" },
    ],
    shortcuts: [
      { to: "/staff/kaai/menu", label: "Publier / remplacer le menu PDF", icon: FileText },
    ],
  },
  {
    key: "lounge",
    label: "Lounge",
    icon: GlassWater,
    intro: "Lounge cocktails, menu PDF et créneaux.",
    sections: [
      { key: "hero_lounge", label: "En-tête immersif (image + textes)" },
    ],
    shortcuts: [
      { to: "/staff/lounge/menu", label: "Publier / remplacer le menu Lounge", icon: FileText },
    ],
  },
  {
    key: "salles",
    label: "Salles / Séminaire",
    icon: Utensils,
    intro: "Salles de réception, séminaires et corporate.",
    sections: [
      { key: "hero_salles", label: "En-tête (titre + description)" },
    ],
    shortcuts: [
      { to: "/staff/meeting-rooms", label: "Configurer les salles", icon: Settings },
      { to: "/staff/corporate-requests", label: "Demandes corporate", icon: Mail },
    ],
  },
  {
    key: "spa",
    label: "Spa & Hammam",
    icon: Sparkles,
    intro: "Actuellement gelé (popup « Bientôt disponible »). Contenu conservé pour réactivation ultérieure.",
    sections: [],
    shortcuts: [],
    frozen: true,
  },
  {
    key: "membership",
    label: "Membership",
    icon: CreditCard,
    intro: "Sunset / Beach / Royal Card. Gelé publiquement, gestion staff prête.",
    sections: [],
    shortcuts: [
      { to: "/staff/memberships", label: "Gérer les cartes membres", icon: CreditCard },
    ],
    frozen: true,
  },
  {
    key: "boutique",
    label: "Boutique",
    icon: ShoppingBag,
    intro: "Souvenirs & articles BBr.",
    sections: [],
    shortcuts: [
      { to: "/staff/media-library", label: "Uploader visuels produits", icon: ImageIcon },
    ],
  },
  {
    key: "blog",
    label: "Blog",
    icon: PenLine,
    intro: "Créer, planifier et publier des articles. Catégories, images, SEO.",
    sections: [],
    shortcuts: [
      { to: "/staff/blog", label: "Gérer les articles", icon: PenLine },
    ],
  },
  {
    key: "contact",
    label: "Contact",
    icon: Mail,
    intro: "Coordonnées, horaires, réseaux, adresses.",
    sections: [
      { key: "hero_contact", label: "En-tête + section « Notre équipe »" },
      { key: "contact", label: "Coordonnées" },
      { key: "footer", label: "Pied de page & réseaux" },
    ],
    shortcuts: [],
  },
  {
    key: "global",
    label: "Paramètres globaux",
    icon: Settings,
    intro: "Navigation, popups, SEO, tracking, mentions légales, livret PDF, médias.",
    sections: [
      { key: "menus_nav", label: "Menus de navigation" },
      { key: "newsletter_popup", label: "Popup Newsletter" },
      { key: "coming_soon", label: "Modal « Bientôt disponible »" },
      { key: "seo", label: "SEO & Méta" },
      { key: "tracking", label: "Tracking (GTM)" },
      { key: "mentions_legales", label: "Mentions légales" },
    ],
    shortcuts: [
      { to: "/staff/livret", label: "Livret BBr (PDF)", icon: BookOpen },
      { to: "/staff/media-library", label: "Bibliothèque médias (images, vidéos, PDF)", icon: Video },
      { to: "/staff/gallery", label: "Galerie photos publique", icon: ImageIcon },
    ],
  },
];

export default function StaffCMS() {
  const [activePage, setActivePage] = useState("accueil");
  const [sections, setSections] = useState({});
  const [loading, setLoading] = useState(true);
  const [openSection, setOpenSection] = useState(null); // key of the currently expanded section

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/staff/site/sections");
      const map = {};
      (data.items || []).forEach((i) => { map[i.key] = i; });
      setSections(map);
    } catch {
      toast.error("Impossible de charger la configuration");
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  const currentPage = useMemo(() => PAGES.find((p) => p.key === activePage), [activePage]);

  return (
    <div className="p-4 md:p-8 lg:p-10 max-w-[1400px] mx-auto space-y-6" data-testid="staff-cms">
      {/* Header */}
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-[0.6rem] tracking-[0.4em] uppercase text-[#0A0A0A]/55 mb-2">
            Vitrine · Configuration
          </div>
          <h1 className="font-serif font-light text-3xl md:text-5xl leading-tight" data-testid="staff-cms-title">
            Configuration du site
          </h1>
          <p className="text-sm text-[#0A0A0A]/65 mt-2 max-w-2xl">
            Administrez chaque page et chaque section de la vitrine BBr en temps réel :
            textes, images, prix, PDF, boutons. Sans intervention technique.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <a href="/" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-3 py-2 border border-[#0A0A0A]/15 text-[0.6rem] tracking-[0.3em] uppercase hover:border-[#B8922A] hover:text-[#B8922A] transition-colors" data-testid="cms-preview-btn">
            <ExternalLink size={12} /> Voir le site
          </a>
          <button onClick={load} className="p-2 border border-[#0A0A0A]/15 hover:border-[#B8922A]" data-testid="cms-refresh-btn">
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-6">
        {/* Left rail — pages */}
        <aside className="border border-[#0A0A0A]/10 bg-white">
          <div className="px-4 py-3 border-b border-[#0A0A0A]/10 text-[0.55rem] tracking-[0.4em] uppercase text-[#0A0A0A]/55">
            Pages du site
          </div>
          <nav>
            {PAGES.map((p) => {
              const Icon = p.icon;
              const isActive = p.key === activePage;
              return (
                <button
                  key={p.key}
                  onClick={() => { setActivePage(p.key); setOpenSection(null); }}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-left text-sm border-b border-[#0A0A0A]/6 transition-colors ${
                    isActive ? "bg-[#FAF7F2] text-[#B8922A] border-l-2 border-l-[#B8922A]" : "hover:bg-[#FAF7F2]/60 text-[#0A0A0A]/80"
                  }`}
                  data-testid={`cms-page-${p.key}`}
                >
                  <Icon size={15} strokeWidth={1.5} />
                  <span className="flex-1">{p.label}</span>
                  {p.frozen && <span className="text-[0.55rem] tracking-[0.2em] uppercase text-amber-700">Gelé</span>}
                </button>
              );
            })}
          </nav>
        </aside>

        {/* Right — sections and shortcuts for the active page */}
        <div className="space-y-5">
          {currentPage && (
            <div className="bg-white border border-[#0A0A0A]/10 p-5 md:p-6">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h2 className="font-serif text-2xl text-[#0A0A0A]" data-testid="cms-page-title">{currentPage.label}</h2>
                  <p className="text-sm text-[#0A0A0A]/60 mt-1 max-w-3xl leading-relaxed">{currentPage.intro}</p>
                </div>
                {currentPage.frozen && (
                  <span className="whitespace-nowrap text-[0.6rem] tracking-[0.3em] uppercase text-amber-700 border border-amber-400 px-2.5 py-1">
                    Accès public gelé
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Sections list */}
          {currentPage?.sections?.length > 0 && (
            <div className="space-y-3">
              <div className="text-[0.55rem] tracking-[0.4em] uppercase text-[#0A0A0A]/55">
                Sections éditables
              </div>
              {currentPage.sections.map((sec) => {
                const configured = !!sections[sec.key]?.updated_by;
                return (
                  <SectionCard
                    key={sec.key}
                    section={sec}
                    open={openSection === sec.key}
                    onToggle={() => setOpenSection(openSection === sec.key ? null : sec.key)}
                    initialData={sections[sec.key]?.data}
                    updatedAt={sections[sec.key]?.updated_at}
                    updatedBy={sections[sec.key]?.updated_by}
                    configured={configured}
                    onSaved={load}
                  />
                );
              })}
            </div>
          )}

          {/* Specialised shortcuts */}
          {currentPage?.shortcuts?.length > 0 && (
            <div className="space-y-3">
              <div className="text-[0.55rem] tracking-[0.4em] uppercase text-[#0A0A0A]/55">
                Éditeurs dédiés
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {currentPage.shortcuts.map((s) => {
                  const Icon = s.icon;
                  return (
                    <Link key={s.to} to={s.to} className="flex items-center gap-3 bg-white border border-[#0A0A0A]/10 hover:border-[#B8922A] hover:bg-[#FAF7F2] px-4 py-3 transition-colors" data-testid={`cms-shortcut-${s.to.replace(/\W+/g, "-")}`}>
                      <div className="w-9 h-9 flex items-center justify-center bg-[#FAF7F2] text-[#B8922A]">
                        <Icon size={16} strokeWidth={1.5} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-[#0A0A0A] truncate">{s.label}</div>
                        <div className="text-[0.6rem] tracking-[0.2em] uppercase text-[#0A0A0A]/40">Ouvrir l&apos;éditeur</div>
                      </div>
                      <ChevronRight size={14} className="text-[#0A0A0A]/30" />
                    </Link>
                  );
                })}
              </div>
            </div>
          )}

          {currentPage && (currentPage.sections?.length || 0) === 0 && (currentPage.shortcuts?.length || 0) === 0 && (
            <div className="bg-white border border-dashed border-[#0A0A0A]/20 p-8 text-center text-sm text-[#0A0A0A]/55" data-testid="cms-page-empty">
              Aucun contenu configurable pour l&apos;instant. Prochain lot d&apos;éditeurs
              en Phase 2 du CMS.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Individual section card — expands to inline SectionEditor ──────
function SectionCard({ section, open, onToggle, initialData, updatedAt, updatedBy, configured, onSaved }) {
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && draft == null) setDraft(initialData || {});
  }, [open, initialData, draft]);

  async function save() {
    setSaving(true);
    try {
      await api.put(`/staff/site/sections/${section.key}`, { data: draft });
      invalidateSiteConfig();
      toast.success("Section publiée — visible immédiatement");
      onSaved?.();
    } catch {
      toast.error("Enregistrement impossible");
    } finally {
      setSaving(false);
    }
  }

  async function resetDefault() {
    if (!window.confirm(`Restaurer les valeurs par défaut de "${section.label}" ?`)) return;
    try {
      const { data } = await api.post(`/staff/site/sections/${section.key}/reset`);
      invalidateSiteConfig();
      setDraft(data.data);
      toast.info("Valeurs par défaut restaurées");
      onSaved?.();
    } catch {
      toast.error("Réinitialisation impossible");
    }
  }

  return (
    <div className="bg-white border border-[#0A0A0A]/10" data-testid={`cms-section-card-${section.key}`}>
      <button
        onClick={onToggle}
        className={`w-full flex items-center gap-3 px-4 md:px-5 py-4 text-left hover:bg-[#FAF7F2]/60 transition-colors ${open ? "border-b border-[#0A0A0A]/10" : ""}`}
      >
        {configured ? (
          <CheckCircle2 size={16} className="text-emerald-600 flex-shrink-0" strokeWidth={1.5} />
        ) : (
          <Circle size={16} className="text-[#0A0A0A]/25 flex-shrink-0" strokeWidth={1.5} />
        )}
        <div className="flex-1 min-w-0">
          <div className="text-sm text-[#0A0A0A] font-medium">{section.label}</div>
          <div className="text-[0.6rem] tracking-[0.22em] uppercase text-[#0A0A0A]/45 mt-0.5">
            {updatedAt ? (
              <>Modifié {String(updatedAt).slice(0, 16).replace("T", " ")}{updatedBy ? ` · ${updatedBy}` : ""}</>
            ) : (
              "Valeurs par défaut · non modifié"
            )}
          </div>
        </div>
        <ChevronDown size={14} className={`transition-transform text-[#0A0A0A]/45 ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="p-4 md:p-6 space-y-5 bg-[#FCFAF6]/40" data-testid={`cms-section-body-${section.key}`}>
          {draft == null ? (
            <div className="text-sm text-[#0A0A0A]/45">Chargement…</div>
          ) : (
            <>
              <SectionEditor section={section.key} value={draft} onChange={setDraft} />
              <details className="pt-4 border-t border-[#0A0A0A]/10">
                <summary className="text-[0.6rem] tracking-[0.3em] uppercase text-[#0A0A0A]/55 cursor-pointer hover:text-[#0A0A0A]">
                  Édition JSON brut (avancé)
                </summary>
                <textarea
                  rows={10}
                  value={JSON.stringify(draft, null, 2)}
                  onChange={(e) => { try { setDraft(JSON.parse(e.target.value)); } catch { /* ignore */ } }}
                  className="mt-3 w-full border border-[#0A0A0A]/15 px-3 py-2 text-[12px] font-mono focus:outline-none focus:border-[#B8922A]"
                />
                <p className="text-[10px] text-[#0A0A0A]/45 mt-1">
                  <AlertCircle size={10} className="inline mr-1" />
                  En cas d&apos;erreur de syntaxe, l&apos;éditeur visuel ci-dessus prime.
                </p>
              </details>

              <div className="flex flex-wrap items-center justify-end gap-2 pt-4 border-t border-[#0A0A0A]/10">
                <button
                  onClick={resetDefault}
                  className="inline-flex items-center gap-2 px-4 py-2 border border-[#0A0A0A]/15 text-[0.6rem] tracking-[0.3em] uppercase hover:border-amber-400 hover:text-amber-700 transition-colors"
                  data-testid={`cms-section-${section.key}-reset`}
                >
                  <Undo2 size={12} /> Restaurer par défaut
                </button>
                <button
                  onClick={save}
                  disabled={saving}
                  className="inline-flex items-center gap-2 px-5 py-2 bg-[#0A0A0A] text-white text-[0.65rem] tracking-[0.3em] uppercase hover:bg-[#B8922A] transition-colors disabled:opacity-50"
                  data-testid={`cms-section-${section.key}-save`}
                >
                  <Save size={13} /> {saving ? "Enregistrement…" : "Publier"}
                </button>
              </div>
              {updatedAt && (
                <p className="text-[10px] tracking-wider uppercase text-[#0A0A0A]/45 text-right">
                  <History size={11} className="inline mr-1" />
                  Dernière modification : {String(updatedAt).slice(0, 16).replace("T", " ")}
                  {updatedBy && ` · par ${updatedBy}`}
                </p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
